/**
 * 챌린지 내보내기 · 가져오기 — **파일로 옮긴다.**
 *
 * 내부망에는 QBase 가 없다. 문제를 파일로 옮기면 로컬 저장소(LiveSQL 백엔드 SQLite)에 들어가고,
 * 화면은 공급원이 바뀐 줄도 모른 채 그대로 돈다(`challengeSource.js` 참조).
 *
 * ## 내보내기는 두 곳에서 퍼온다
 *
 *  · **QBase** — 브라우저가 QBase 를 직접 읽으므로 **여기서** 조립한다.
 *  · **로컬**  — 백엔드가 이미 갖고 있으므로 백엔드가 조립해 준다.
 *
 * 어느 쪽이든 **형식은 하나**다. 형식이 두 벌이 되면 한쪽만 고치는 날이 온다 —
 * 그래서 QBase 쪽도 백엔드와 같은 정의(`serializeForFile`)를 쓴다.
 *
 * ## 첨부 파일은 따라가지 않는다
 *
 * 첨부 바이너리는 QBase 의 다운로드 API 에만 있다. 목록은 옮기되 파일은 안 간다 —
 * 가져온 쪽에서 누르면 없다. 그 사실을 경고로 돌려준다(조용히 깨진 채 두지 않는다).
 */
import { attrForExport } from '../utils/challengeAttr';
import axios from 'axios';
import { LOCAL_API, getSource, LOCAL } from './challengeSource';
import { listChallenges, listQuiz } from './livesqlRepo';

/** 백엔드 `exchange.js` 와 **같은 형식**. 바꿀 때는 양쪽을 함께 바꾼다. */
const FORMAT_TYPE = 'livesql-challenges';
const FORMAT_VERSION = '1.0';

/** 챌린지 헤더에서 파일에 담는 필드. 모르는 필드도 함께 실린다(아래 스프레드). */
const pickChallenge = (c) => ({
  ...c,
  challengeId: c.QRY_CHALLENGE_ID ?? c.qryChallengeId ?? c.challengeId ?? null,
  challengeNm: c.CHALLENGE_NM ?? c.qryChallengeNm ?? c.challengeNm ?? null,
  challengeLevelDcd: c.CHALLENGE_LEVEL_DCD ?? c.challengeLevelDcd ?? null,
  bgngDt: c.BGNG_DT ?? c.bgngDt ?? c.startDt ?? null,
  endDt: c.END_DT ?? c.endDt ?? null,
  publicYn: c.PUBLIC_YN ?? c.publicYn ?? c.openYn ?? null,
  mgrUsrid: c.MGR_USRID ?? c.mgrUsrid ?? null,
});

const pickQuiz = (q) => ({
  ...q,
  quizNo: q.QUIZ_NO ?? q.quizNo ?? null,
  quizTcd: q.QUIZ_TCD ?? q.quizTcd ?? null,
  quizDc: q.QUIZ_DC ?? q.quizDc ?? null,
  quizHint: q.QUIZ_HINT ?? q.quizHint ?? null,
  cransSqlCn: q.CRANS_SQL_CN ?? q.cransSqlCn ?? null,
  cransRstCn: q.CRANS_RST_CN ?? q.cransRstCn ?? null,
  addCransVerifCn: q.ADD_CRANS_VERIF_CN ?? q.addCransVerifCn ?? null,
  atchFileLstCn: q.ATCH_FILE_LST_CN ?? q.atchFileLstCn ?? null,
});

function serializeForFile(challenges, source) {
  const list = challenges.map((c) => {
    const quizzes = (c.quizzes || []).map(pickQuiz);
    // 챌린지 속성(종류·DBMS) — 적어 둔 것이 없으면 문항으로 추론해 적어 준다(받는 쪽이 다시 추론하지 않게, utils/challengeAttr)
    return { ...pickChallenge(c), challengeAttrCn: attrForExport(c, quizzes), quizzes };
  });
  return {
    version: FORMAT_VERSION,
    type: FORMAT_TYPE,
    exportedAt: new Date().toISOString(),
    source,
    challengeCount: list.length,
    quizCount: list.reduce((n, c) => n + c.quizzes.length, 0),
    challenges: list,
  };
}

/**
 * 지금 보고 있는 공급원에서 챌린지를 모아 파일 내용을 만든다.
 *
 * @param challengeIds 고른 것만. 비우면 전부.
 * @returns {{ doc, source }}
 */
export async function buildExport(challengeIds) {
  // **지금 보고 있는 출처에서** 퍼온다. 화면에 QBase 목록을 띄워 놓고 로컬 것이 나가면
  // 사용자는 무엇을 내보냈는지 알 수 없다(2026-09-19: 자동 판정을 걷어낸 것과 같은 이유).
  if (getSource() === LOCAL) {
    const res = await axios.get(`${LOCAL_API}/challenges/export`,
      { params: challengeIds && challengeIds.length ? { ids: challengeIds.join(',') } : undefined });
    return { doc: res.data, source: 'local' };
  }

  // QBase 에서 퍼온다. 문항은 챌린지마다 따로 받아야 한다(목록 API 가 주지 않는다).
  const rows = await listChallenges();
  const wanted = (challengeIds && challengeIds.length)
    ? rows.filter(c => challengeIds.includes(String(c.QRY_CHALLENGE_ID ?? c.qryChallengeId ?? c.challengeId)))
    : rows;

  const withQuiz = [];
  for (const c of wanted) {
    const id = c.QRY_CHALLENGE_ID ?? c.qryChallengeId ?? c.challengeId;
    const res = await listQuiz(id);
    withQuiz.push({ ...c, quizzes: res.qryChallengeQuizLst || [] });
  }
  return { doc: serializeForFile(withQuiz, 'qbase'), source: 'qbase' };
}

/**
 * 편집기가 고칠 챌린지 하나 — 지금 보고 있는 출처에서 **문항까지** 교환 형식으로 퍼온다(2026-10-05 챌린지 편집기).
 * 내보내기와 같은 길을 지나므로 편집기가 받는 모양이 파일 형식과 같다.
 */
export async function loadChallengeForEdit(challengeId) {
  const { doc } = await buildExport([String(challengeId)]);
  const c = (doc.challenges || [])[0];
  if (!c) throw new Error(`challenge not found: ${challengeId}`);
  return c;
}

/** 편집기가 만든 챌린지 하나(교환 형식, 문항 포함)를 파일 문서로 감싼다 — 내보내기·가져오기와 같은 형식 */
export function wrapChallengeDoc(challenge) {
  return {
    version: FORMAT_VERSION,
    type: FORMAT_TYPE,
    exportedAt: new Date().toISOString(),
    source: 'editor',
    challengeCount: 1,
    quizCount: (challenge.quizzes || []).length,
    challenges: [challenge],
  };
}

/** 파일로 내려받는다. 이름에 날짜를 넣어 여러 벌이 섞이지 않게 한다. */
export function downloadExport(doc, baseName = 'livesql-challenges') {
  const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${baseName}-${stamp}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * 파일 내용을 로컬 저장소에 넣는다.
 * @returns {{ challengeCount, quizCount, warnings }}
 */
export async function importDoc(doc) {
  // 가져오기는 **언제나 로컬로** 들어간다(QBase 에 쓰는 길은 없다). 화면 전환은 부르는 쪽이
  // 눈에 보이게 한다 — 여기서 조용히 바꾸지 않는다.
  const res = await axios.post(`${LOCAL_API}/challenges/import`, doc);
  return res.data;
}
